/**
 * Fase 38 — API client del Offline Session Analysis (findings).
 *
 * Endpoints consumidos:
 *   GET    /admin/findings             — listar con filtros.
 *   GET    /admin/findings/:id         — detalle con evidence + proposed_fix.
 *   PATCH  /admin/findings/:id         — actualizar status / notes / cluster.
 *   GET    /admin/findings/runs/list   — historial de runs.
 *   POST   /admin/findings/sessions/:id/analyze — re-run idempotente.
 */
import { get, patchJsonAdmin, postJsonAdmin } from './_core.js';

export interface FindingEvidenceQuote {
  turn_index: number;
  speaker: 'user' | 'assistant' | 'system' | 'tool';
  text: string;
  span_id?: string;
}

export interface FindingEvidence {
  quotes: FindingEvidenceQuote[];
  tool_calls?: Array<Record<string, unknown>>;
  metrics?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

export interface ProposedFix {
  target: string;
  scope?: Record<string, unknown>;
  before?: string;
  after?: string;
  rationale: string;
}

export interface FindingListItem {
  id: string;
  runId: string;
  sessionId: string;
  userId: string;
  moduleSlug: string;
  traceId: string | null;
  category: string | null;
  severity: number;
  /** null cuando el finding es de un módulo sensible (`gated: true`). */
  title: string | null;
  /** null cuando el finding es de un módulo sensible (`gated: true`). */
  description: string | null;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewerId: string | null;
  /** Fase 39 — true cuando la sesión origen era sintética. */
  fromSynthetic: boolean;
  /**
   * Fase 38.5b — finding original al que este regression-of apunta. NULL
   * cuando NO es regression (el matcher no encontró match en el registry
   * de fixes shipped).
   */
  regressionOf: string | null;
  /**
   * Gate de privacidad de módulos sensibles: el contenido textual
   * (title/description/evidence/proposedFix) viene nulificado; la metadata
   * operativa queda para triage. Ausente cuando no aplica.
   */
  gated?: boolean;
}

export interface FindingDetail extends FindingListItem {
  /** null cuando el finding es de un módulo sensible (`gated: true`). */
  evidence: FindingEvidence | null;
  proposedFix: ProposedFix | null;
  reviewerNotes: string | null;
  clusterId: string | null;
  updatedAt: string;
}

export interface FindingsListResponse {
  items: FindingListItem[];
}

export interface FindingsListFilters {
  status?: string;
  minSeverity?: number;
  category?: string;
  module?: string;
  sessionId?: string;
  limit?: number;
  /** Fase 39 — default false: ocultar findings de sesiones sintéticas. */
  includeSynthetic?: boolean;
  /**
   * Sólo las regressions de este finding original (uuid). Es el filtro del
   * link "ver regressions" de ShippedFixes (`/findings?regression_of=<id>`).
   */
  regressionOf?: string;
}

function buildQuery(filters: FindingsListFilters): string {
  const params: string[] = [];
  if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`);
  if (typeof filters.minSeverity === 'number')
    params.push(`min_severity=${filters.minSeverity}`);
  if (filters.category) params.push(`category=${encodeURIComponent(filters.category)}`);
  if (filters.module) params.push(`module=${encodeURIComponent(filters.module)}`);
  if (filters.sessionId)
    params.push(`session_id=${encodeURIComponent(filters.sessionId)}`);
  if (filters.limit) params.push(`limit=${filters.limit}`);
  if (filters.includeSynthetic) params.push(`include_synthetic=true`);
  if (filters.regressionOf)
    params.push(`regression_of=${encodeURIComponent(filters.regressionOf)}`);
  return params.length > 0 ? `?${params.join('&')}` : '';
}

export async function fetchFindings(
  filters: FindingsListFilters = {},
): Promise<FindingsListResponse> {
  const res = await get<FindingsListResponse>(`/admin/findings${buildQuery(filters)}`);
  return res.data;
}

export async function fetchFinding(id: string): Promise<FindingDetail> {
  const res = await get<FindingDetail>(`/admin/findings/${encodeURIComponent(id)}`);
  return res.data;
}

export interface UpdateFindingInput {
  status?: string;
  reviewerNotes?: string | null;
  clusterId?: string | null;
}

export async function updateFinding(id: string, patch: UpdateFindingInput): Promise<{ id: string }> {
  return patchJsonAdmin<{ id: string }>(`/admin/findings/${encodeURIComponent(id)}`, patch);
}

export interface AnalyzeRunListItem {
  id: string;
  sessionId: string;
  userId: string;
  moduleSlug: string;
  traceId: string | null;
  startedAt: string;
  endedAt: string | null;
  status: string;
  errorText: string | null;
  iterations: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalProviderCostNano: string;
  findingsProposed: number;
  findingsAccepted: number;
  plannerModel: string | null;
  readerModel: string | null;
  criticModel: string | null;
  /**
   * Veredicto textual del Reader (por qué cerró con N findings) — crítico
   * cuando N=0: distingue "sesión sana (razonado)" de "no pude investigar".
   * null en runs previas a la mig 0314, si el Reader no dijo nada, o si el
   * run es de un módulo sensible bajo el gate de privacidad (`gated`).
   */
  readerSummary: string | null;
  /** true cuando el gate de módulos sensibles nulificó el contenido textual. */
  gated?: boolean;
}

export async function fetchAnalyzeRuns(
  sessionId?: string,
): Promise<{ items: AnalyzeRunListItem[] }> {
  const qs = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
  const res = await get<{ items: AnalyzeRunListItem[] }>(`/admin/findings/runs/list${qs}`);
  return res.data;
}

export async function enqueueAnalyzeRun(
  sessionId: string,
): Promise<{ sessionId: string; jobId: string }> {
  return postJsonAdmin<{ sessionId: string; jobId: string }>(
    `/admin/findings/sessions/${encodeURIComponent(sessionId)}/analyze`,
    {},
  );
}

export interface RecordActionInput {
  actionType: string;
  target: Record<string, unknown>;
  diff: Record<string, unknown>;
  commitSha?: string;
  notes?: string;
}

export async function recordFindingAction(
  id: string,
  input: RecordActionInput,
): Promise<{ actionId: string }> {
  return postJsonAdmin<{ actionId: string }>(
    `/admin/findings/${encodeURIComponent(id)}/actions`,
    input,
  );
}

export interface PromoteInput {
  scenario: Record<string, unknown>;
  expectedBehavior: string;
}

export async function promoteFinding(
  id: string,
  input: PromoteInput,
): Promise<{ datasetId: string }> {
  return postJsonAdmin<{ datasetId: string }>(
    `/admin/findings/${encodeURIComponent(id)}/promote`,
    input,
  );
}

/**
 * Fase 38.5b — un fix shipped (entrada del `analyze_eval_dataset`) + count
 * de regressions detectadas pasivamente contra ese fix. No hay más concepto
 * de "regression run" (el cron MVP fue retirado); la detección ocurre
 * automáticamente al final de `session.analyze` en sesiones reales nuevas.
 */
export interface ShippedFixItem {
  id: string;
  sourceFindingId: string | null;
  sourceSessionId: string | null;
  moduleSlug: string;
  category: string | null;
  expectedBehavior: string;
  addedAt: string;
  active: boolean;
  /**
   * Cuántas regressions (findings no-sintéticos con
   * `regression_of=sourceFindingId`, sin ventana temporal) — paridad exacta
   * con el listado default que abre el link del badge (ítem 50).
   */
  regressionCount: number;
  /** ISO del último finding regression detectado, o null si nunca. */
  latestRegressionAt: string | null;
}

export async function fetchShippedFixes(
  module?: string,
): Promise<{ items: ShippedFixItem[] }> {
  const qs = module ? `?module=${encodeURIComponent(module)}` : '';
  const res = await get<{ items: ShippedFixItem[] }>(`/admin/findings/shipped/list${qs}`);
  return res.data;
}
